// Voting, and why it is not majority rule.
//
// A crew of three or four ties constantly, and a majority gives nobody a way to
// say that one set matters far more to them than the others. Priority tokens fix
// that with scarcity: a small fixed allocation for the whole festival, so spending
// one is a credible signal in a way that saying it again is not. Bluffing costs
// something, so preferences come out closer to true.
//
// Token spends are visible to the crew on purpose. Half the value of a token is
// the social signal it sends.
//
// Non-response is consent, not obstruction. A crew where one member has stopped
// checking their phone still needs to be able to decide things.

import type { TuningConfig } from './config.ts';
import { DEFAULT_CONFIG } from './config.ts';
import type { Conflict, Id, LedgerRow, Vote, WorkspaceSettings } from './types.ts';

export type ResolutionRule = 'hard_lock' | 'tokens' | 'weighted' | 'regret_tiebreak' | 'no_votes';

export interface Resolution {
  optionId: Id | null;
  rule: ResolutionRule;
  explanation: string;
  /** Tokens actually consumed, by member, if the token rule decided it. */
  tokensSpent: Record<Id, number>;
}

export interface VoteInput {
  conflict: Conflict;
  votes: Vote[];
  ledger: LedgerRow[];
  settings: WorkspaceSettings;
  cfg?: TuningConfig;
}

/** Resolves a conflict in the fixed order: locks, tokens, weights, then regret. */
export function resolveVote(input: VoteInput): Resolution {
  const cfg = input.cfg ?? DEFAULT_CONFIG;
  const { conflict, votes, ledger } = input;
  const counted = votes.filter((v) => !v.pending);
  const tokensSpent: Record<Id, number> = {};

  const lockHolders = conflict.memberPositions.filter((p) => p.hasHardLock);
  if (lockHolders.length === 1 && !conflict.lockCollision) {
    const holder = lockHolders[0];
    const option = conflict.options.find(
      (o) => o.feasible && o.segments.some((s) => s.setId === holder.preferredSetId),
    );
    if (option) {
      return {
        optionId: option.id,
        rule: 'hard_lock',
        explanation: `${holder.displayName} locked this set and nobody else holds a lock in the window.`,
        tokensSpent,
      };
    }
  }

  const byOption = new Map<Id, { tokens: number; weighted: number; voters: string[] }>();
  const weights = new Map(ledger.map((r) => [r.userId, ledgerWeightOf(r, ledger, cfg)]));

  for (const v of counted) {
    const entry = byOption.get(v.optionId) ?? { tokens: 0, weighted: 0, voters: [] };
    const w = weights.get(v.userId) ?? 1;
    if (v.tokenSpent) {
      entry.tokens += 1;
      tokensSpent[v.userId] = (tokensSpent[v.userId] ?? 0) + 1;
    }
    entry.weighted += w;
    const name = conflict.memberPositions.find((p) => p.userId === v.userId)?.displayName ?? v.userId;
    entry.voters.push(name);
    byOption.set(v.optionId, entry);
  }

  if (byOption.size === 0) {
    return {
      optionId: conflict.recommendedOptionId ?? null,
      rule: 'no_votes',
      explanation: 'Nobody voted before the window closed, so the recommendation stands.',
      tokensSpent,
    };
  }

  const entries = [...byOption.entries()];
  const maxTokens = Math.max(...entries.map(([, e]) => e.tokens));
  if (maxTokens > 0) {
    const leaders = entries.filter(([, e]) => e.tokens === maxTokens);
    if (leaders.length === 1) {
      const [optionId, e] = leaders[0];
      const verb = input.settings.tokensForceOutcome ? 'forces' : 'carries';
      return {
        optionId,
        rule: 'tokens',
        explanation: `${e.voters.join(' and ')} spent a priority token, which ${verb} this outcome.`,
        tokensSpent,
      };
    }
  }

  const maxWeighted = Math.max(...entries.map(([, e]) => e.weighted));
  const weightedLeaders = entries.filter(([, e]) => Math.abs(e.weighted - maxWeighted) < 1e-9);
  if (weightedLeaders.length === 1) {
    const [optionId, e] = weightedLeaders[0];
    return {
      optionId,
      rule: 'weighted',
      explanation: `${e.voters.join(' and ')} carried it once the ledger weights were applied.`,
      tokensSpent,
    };
  }

  const tiedUsers = counted
    .filter((v) => weightedLeaders.some(([id]) => id === v.optionId))
    .map((v) => ledger.find((r) => r.userId === v.userId))
    .filter((r): r is LedgerRow => Boolean(r))
    .sort((a, b) => b.cumulativeRegret - a.cumulativeRegret);

  const winner = tiedUsers[0];
  const winningVote = counted.find((v) => v.userId === winner?.userId);
  const name =
    conflict.memberPositions.find((p) => p.userId === winner?.userId)?.displayName ??
    'the member who has given way most';
  return {
    optionId: winningVote?.optionId ?? weightedLeaders[0][0],
    rule: 'regret_tiebreak',
    explanation: `The vote tied, so it went to ${name}, who has given way most so far this weekend.`,
    tokensSpent,
  };
}

function ledgerWeightOf(row: LedgerRow, all: LedgerRow[], cfg: TuningConfig): number {
  const mean = all.reduce((a, r) => a + r.decayedRegret, 0) / Math.max(1, all.length);
  if (mean <= 0) return 1;
  const [lo, hi] = cfg.ledgerWeightClamp;
  return Math.max(lo, Math.min(hi, 1 + cfg.ledgerAlpha * (row.decayedRegret / mean - 1)));
}

/** Removes the tokens a resolution consumed from the ledger. */
export function spendTokens(ledger: LedgerRow[], spent: Record<Id, number>): LedgerRow[] {
  return ledger.map((r) => {
    const n = spent[r.userId] ?? 0;
    return n > 0 ? { ...r, tokensRemaining: Math.max(0, r.tokensRemaining - n) } : r;
  });
}

/** Says whether a member may still spend a token on this conflict. */
export function canSpendToken(
  ledger: LedgerRow[],
  userId: Id,
  votes: Vote[],
  conflictId: Id,
): boolean {
  const row = ledger.find((r) => r.userId === userId);
  if (!row || row.tokensRemaining <= 0) return false;
  return !votes.some((v) => v.userId === userId && v.conflictId === conflictId && v.tokenSpent);
}
